import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { UserPlus, Building2, Loader2 } from "lucide-react";
import { Layout } from "@/components/Layout";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const isSignup = mode === "signup";
  const [params] = useSearchParams();
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState(params.get("role") === "employer" ? "employer" : "candidate");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      let user;
      if (isSignup) user = await register({ ...form, role });
      else user = await login(form.email, form.password);
      toast.success(isSignup ? "Welcome to GetJobs.Today!" : "Welcome back!");
      nav(user.role === "employer" ? "/employer" : user.role === "admin" ? "/admin" : "/dashboard");
    } catch (e2) {
      const msg = formatApiError(e2.response?.data?.detail) || e2.message;
      setErr(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <Layout hideFooter>
      <div className="max-w-md mx-auto px-4 py-12 sm:py-16">
        <h1 className="font-display text-3xl font-extrabold text-navy text-center">
          {isSignup ? "Create your account" : "Welcome back"}
        </h1>
        <p className="mt-2 text-center text-sm text-slate-500">
          {isSignup ? "Candidates are free for life. Employers get 5 free job posts." : "Log in to continue"}
        </p>

        {isSignup && (
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button type="button" data-testid="role-candidate" onClick={() => setRole("candidate")}
              className={`gjt-card flex flex-col items-center gap-2 p-4 ${role === "candidate" ? "ring-2 ring-brand-blue" : ""}`}>
              <UserPlus className="h-5 w-5 text-brand-blue" />
              <span className="text-sm font-bold text-navy">I want work</span>
            </button>
            <button type="button" data-testid="role-employer" onClick={() => setRole("employer")}
              className={`gjt-card flex flex-col items-center gap-2 p-4 ${role === "employer" ? "ring-2 ring-brand-orange" : ""}`}>
              <Building2 className="h-5 w-5 text-brand-orange" />
              <span className="text-sm font-bold text-navy">I need people</span>
            </button>
          </div>
        )}

        <form onSubmit={submit} className="mt-6 space-y-4">
          {isSignup && (
            <div>
              <label className="text-sm font-semibold text-navy">{role === "employer" ? "Company name" : "Full name"}</label>
              <input data-testid="auth-name" required value={form.name} onChange={upd("name")}
                className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/20" placeholder={role === "employer" ? "Acme Inc." : "Your name"} />
            </div>
          )}
          <div>
            <label className="text-sm font-semibold text-navy">Email</label>
            <input data-testid="auth-email" type="email" required value={form.email} onChange={upd("email")}
              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/20" placeholder="you@example.com" />
          </div>
          <div>
            <label className="text-sm font-semibold text-navy">Password</label>
            <input data-testid="auth-password" type="password" required value={form.password} onChange={upd("password")}
              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/20" placeholder="••••••••" />
          </div>
          {err && <p data-testid="auth-error" className="text-sm font-medium text-red-500">{err}</p>}
          <button type="submit" disabled={busy} data-testid="auth-submit"
            className="gjt-btn-orange w-full rounded-xl py-3 text-sm font-bold disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {isSignup ? "Create free account" : "Log in"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-500">
          {isSignup ? (
            <>Already have an account? <Link to="/login" className="font-semibold text-brand-blue">Log in</Link></>
          ) : (
            <>New here? <Link to="/signup" className="font-semibold text-brand-blue">Create a free account</Link></>
          )}
        </p>
        {!isSignup && (
          <div className="mt-4 rounded-xl bg-slate-50 p-3 text-center text-xs text-slate-500">
            Demo: candidate@demo.com / employer@demo.com — password <b>Demo@1234</b>
          </div>
        )}
      </div>
    </Layout>
  );
}
